// The three sectors of a run (Phaser-free data). Each sector is one biome with its own
// boss, music, ambience, hazard flavour, enemy mix and palette. Environment colours stay
// dark and desaturated; accents are reserved for signage, hazards and light sources.

import { tilesetKey, parallaxKey } from '../art/keys';
import { PARALLAX_LAYERS, type ParallaxLayer } from '../config/constants';
import type { SectorDef } from '../core/sector';
import type { BiomeId } from '../core/types';

const parallaxFor = (biome: BiomeId): Record<ParallaxLayer, string> =>
  Object.fromEntries(PARALLAX_LAYERS.map((layer) => [layer, parallaxKey(biome, layer)])) as Record<ParallaxLayer, string>;

/** One entry per sector of a run, index 0..SECTORS_PER_RUN-1. */
export const SECTORS: SectorDef[] = [
  {
    index: 0,
    biome: 'graveyard',
    name: 'NEON GRAVEYARD',
    subtitle: 'SECTOR 01 // RUINED MEGACITY',
    boss: 'warden',
    music: 'graveyard',
    parallax: parallaxFor('graveyard'),
    tileset: tilesetKey('graveyard'),
    ambient: 'rain',
    hazard: 'electric',
    enemyPool: [
      { id: 'trooper', weight: 5 },
      { id: 'hornet', weight: 4 },
      { id: 'lancer', weight: 1.6 },
      { id: 'bulwark', weight: 1.2 },
    ],
    eliteAffixes: ['shielded', 'swift', 'overcharged'],
    difficulty: 1,
    palette: {
      skyTop: 0x05070d,
      skyBottom: 0x141b2b,
      fog: 0x2a3550,
      accent: 0x35e8ff, // cold cyan signage
      accent2: 0xff3d9a, // broken magenta advertisements
      tileTint: 0xd4dbe6,
      light: 0x9fb8d8,
    },
  },
  {
    index: 1,
    biome: 'foundry',
    name: 'SKY FOUNDRY',
    subtitle: 'SECTOR 02 // FLOATING ASSEMBLY',
    boss: 'foundry_titan',
    music: 'foundry',
    parallax: parallaxFor('foundry'),
    tileset: tilesetKey('foundry'),
    ambient: 'embers',
    hazard: 'molten',
    enemyPool: [
      { id: 'trooper', weight: 3.5 },
      { id: 'hornet', weight: 3.5 },
      { id: 'lancer', weight: 2.6 },
      { id: 'bulwark', weight: 2.2 },
    ],
    eliteAffixes: ['overcharged', 'volatile', 'shielded', 'swift'],
    difficulty: 1.35,
    palette: {
      skyTop: 0x0d0706,
      skyBottom: 0x2a160e,
      fog: 0x4a2a1a,
      accent: 0xff8a2a, // molten orange
      accent2: 0xffd27a, // sodium work lights
      tileTint: 0xe6d8cc,
      light: 0xffb27a,
    },
  },
  {
    index: 2,
    biome: 'vault',
    name: 'SINGULARITY VAULT',
    subtitle: 'SECTOR 03 // REACTOR CORE',
    boss: 'singularity_heart',
    music: 'vault',
    parallax: parallaxFor('vault'),
    tileset: tilesetKey('vault'),
    ambient: 'motes',
    hazard: 'void',
    enemyPool: [
      { id: 'trooper', weight: 3 },
      { id: 'hornet', weight: 3 },
      { id: 'lancer', weight: 3 },
      { id: 'bulwark', weight: 2.6 },
    ],
    eliteAffixes: ['volatile', 'overcharged', 'swift', 'shielded'],
    difficulty: 1.75,
    palette: {
      skyTop: 0x06040d,
      skyBottom: 0x170f2a,
      fog: 0x2e1f4d,
      accent: 0xb266ff, // singularity violet
      accent2: 0x5cf2ff, // containment cyan
      tileTint: 0xd8d2e8,
      light: 0xc6a8ff,
    },
  },
];
